import {Component, OnInit, Output, EventEmitter, Input, ViewChild, AfterViewInit} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SocialAuthService } from 'angularx-social-login';
import { CookieService } from 'ngx-cookie-service';
import { getStorage } from 'src/app/utils/helper/storage';
import { CURRENT_USER, ON_CALL, TOKEN } from 'src/app/utils/items/storage-names';
import { ApiService } from 'src/app/utils/services/api.service';
import { AppService } from 'src/app/utils/services/app.service';
import { ScheduleService } from 'src/app/utils/services/schedule/schedule.service';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CompanySwitchModalComponent } from '../company-switch-modal/company-switch-modal.component';
import { DeleteConfirmationComponent } from '../delete-confirmation/delete-confirmation.component';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr'; // Added ToastrService import

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, AfterViewInit {

  @Input() sidebarMenuOpened: boolean;
  @Input() isOnboarding = false;
  @Output() toggleMenuSidebar: EventEmitter<any> = new EventEmitter<any>();
  @Output() companyChanged: EventEmitter<any> = new EventEmitter<any>(); // New event emitter for company change
  @ViewChild('companySwitchModal', { static: false }) companySwitchModal: CompanySwitchModalComponent;
  @ViewChild('confirmSwitchModal', { static: false }) confirmSwitchModal: DeleteConfirmationComponent;

  public searchForm: FormGroup;

  user: any;
  darkMode = false;
  currentDate: any;
  selectedCompany: any = null;
  pendingCompany: any = null;
  showCompanySwitch = true; // New property to control visibility of company switch
  companies: any[] = []; // Add companies array

  constructor(
    private _appService: AppService,
    private apiService: ApiService,
    private service: ScheduleService,
    private cookieService: CookieService,
    private router: Router,
    private http: HttpClient,
    private toastr: ToastrService // Added ToastrService injection
  ) {
    this.user = this._appService.getCurrentUser();
  }

  ngOnInit() {
    this.currentDate = new Date();
    this._appService.userSubject
      .pipe(filter(user => !!user))
      .subscribe((user: any) => {
        this.user = user;
        // Check if company switch should be shown based on is_onelife flag
        this.checkCompanySwitchVisibility();
        // Also reload selected company when user changes
        this.loadSelectedCompany();
      });

    this.darkMode = this.cookieService.get('settings.dark-mode') ? true : false;
    this.searchForm = new FormGroup({
      search: new FormControl(null)
    });
    
    // Load companies data
    this.loadCompanies();
    
    // Load selected company from localStorage if exists
    this.loadSelectedCompany();
    
    // Check if company switch should be shown based on is_onelife flag
    this.checkCompanySwitchVisibility();
  }

  /**
   * Load companies data from JSON file
   */
  loadCompanies(): void {
    this.http.get('./assets/data/companies.json').subscribe(
      (data: any[]) => {
        this.companies = data;
        console.log('Loaded companies data:', this.companies);
        // Update selected company with full data if it exists
        this.updateSelectedCompanyWithFullData();
      },
      (error) => {
        console.error('Error loading companies data:', error);
      }
    );
  }

  /**
   * Update selected company with full data from companies array
   */
  updateSelectedCompanyWithFullData(): void {
    if (this.selectedCompany && this.companies.length > 0) {
      const fullCompanyData = this.companies.find(company => company.id === this.selectedCompany.id);
      if (fullCompanyData) {
        this.selectedCompany = fullCompanyData;
        console.log('Updated selected company with full data:', this.selectedCompany);
      } else {
        // If company not found, check is_onelife flag to determine default
        const isOnelifeStr = localStorage.getItem('is_onelife');
        let isOnelife = false;
        if (isOnelifeStr !== null) {
          try {
            isOnelife = JSON.parse(isOnelifeStr);
          } catch (e) {
            console.error('Error parsing is_onelife from localStorage:', e);
          }
        }
        
        // If is_onelife is true, default to company ID 2 (1Life), otherwise default to company ID 1 (mWell)
        if (isOnelife) {
          // Find company with ID 2 (1Life)
          const onelifeCompany = this.companies.find(company => company.id === 2);
          this.selectedCompany = onelifeCompany || this.companies[0];
        } else {
          // Find company with ID 1 (mWell)
          const mwellCompany = this.companies.find(company => company.id === 1);
          this.selectedCompany = mwellCompany || this.companies[0];
        }
        console.log('Company not found, defaulted based on is_onelife flag:', this.selectedCompany);
      }
    } else if (this.companies.length > 0) {
      // If no selected company but we have companies, default based on is_onelife flag
      const isOnelifeStr = localStorage.getItem('is_onelife');
      let isOnelife = false;
      if (isOnelifeStr !== null) {
        try {
          isOnelife = JSON.parse(isOnelifeStr);
        } catch (e) {
          console.error('Error parsing is_onelife from localStorage:', e);
        }
      }
      
      // If is_onelife is true, default to company ID 2 (1Life), otherwise default to company ID 1 (mWell)
      if (isOnelife) {
        // Find company with ID 2 (1Life)
        const onelifeCompany = this.companies.find(company => company.id === 2);
        this.selectedCompany = onelifeCompany || this.companies[0];
      } else {
        // Find company with ID 1 (mWell)
        const mwellCompany = this.companies.find(company => company.id === 1);
        this.selectedCompany = mwellCompany || this.companies[0];
      }
      console.log('No selected company, defaulted based on is_onelife flag:', this.selectedCompany);
    }
  }

  /**
   * Load selected company from localStorage
   */
  loadSelectedCompany(): void {
    const storedCompany = localStorage.getItem('selectedCompany');
    if (storedCompany) {
      try {
        this.selectedCompany = JSON.parse(storedCompany);
        console.log('Loaded selected company from localStorage:', this.selectedCompany);
        // Update with full company data
        this.updateSelectedCompanyWithFullData();
      } catch (e) {
        console.error('Error parsing selected company from localStorage:', e);
      }
    } else {
      // Check if is_onelife is true to set default company to ID 2
      const isOnelifeStr = localStorage.getItem('is_onelife');
      let isOnelife = false;
      if (isOnelifeStr !== null) {
        try {
          isOnelife = JSON.parse(isOnelifeStr);
        } catch (e) {
          console.error('Error parsing is_onelife from localStorage:', e);
        }
      }
      
      // If is_onelife is true, default to company ID 2 (1Life), otherwise default to company ID 1 (mWell)
      if (this.companies.length > 0) {
        if (isOnelife) {
          // Find company with ID 2 (1Life)
          const onelifeCompany = this.companies.find(company => company.id === 2);
          this.selectedCompany = onelifeCompany || this.companies[0];
        } else {
          // Find company with ID 1 (mWell)
          const mwellCompany = this.companies.find(company => company.id === 1);
          this.selectedCompany = mwellCompany || this.companies[0];
        }
        console.log('Set default company based on is_onelife flag:', this.selectedCompany);
      }
    }
  }

  /**
   * Check if company switch should be visible based on is_onelife flag
   */
  checkCompanySwitchVisibility(): void {
    // First check if user object has is_onelife property directly
    if (this.user?.doctor?.is_onelife !== undefined) {
      this.showCompanySwitch = this.user.doctor.is_onelife;
      // Also update localStorage for consistency
      localStorage.setItem('is_onelife', JSON.stringify(this.user.doctor.is_onelife));
      return;
    }
    
    // Get is_onelife value from localStorage (set during login)
    const isOnelifeStr = localStorage.getItem('is_onelife');
    if (isOnelifeStr !== null) {
      try {
        const isOnelife = JSON.parse(isOnelifeStr);
        // If is_onelife is true, show the company switch
        // If is_onelife is false, hide the company switch
        this.showCompanySwitch = isOnelife;
      } catch (e) {
        console.error('Error parsing is_onelife from localStorage:', e);
        // Default to showing company switch if there's an error
        this.showCompanySwitch = true;
      }
    } else {
      // Default to showing company switch if not set
      this.showCompanySwitch = true;
    }
  }

  ngAfterViewInit() {
    console.log('Header component view initialized');
    console.log('Company switch modal:', this.companySwitchModal);
    console.log('Confirm switch modal:', this.confirmSwitchModal);
  }

  toggleOnCall(toggle, event) {
    this.service.updateOnCallStatus(toggle.service_item_code, toggle?.resource_id, event.checked ? 1 : 0)
      .subscribe();
    localStorage.setItem(CURRENT_USER, JSON.stringify(this.user));
  }

  switchToMwellBasic() {
    let url = environment.mwell_basic;

    if (url.indexOf('platform') === -1) {
      url += '/onboard/dashboard';
    }

    location.href = url;
  }

  renderName(user){

    if (user?.role_name === 'staff'){
      if (user?.suffix){
        return user?.full;
      }
      else{
        return user?.full.replace('null','');
      }
    }
    else{
      if (user?.suffix){
        return user?.doctor?.primary_name?.full;
      }
      else{
        return user?.doctor?.primary_name?.full.replace('null','');
      }
    }
  }

  logout() {
    this.apiService.logout()
      .subscribe((response: any) => {
        this.router.navigate(['/logout']);
      }, (err) => {
      });
  }
  
  openCompanySwitchModal() {
    console.log('Attempting to open company switch modal');
    // Add a small delay to ensure the ViewChild is properly initialized
    setTimeout(() => {
      if (this.companySwitchModal) {
        console.log('Company switch modal reference exists');
        // Try to open the modal
        try {
          this.companySwitchModal.open();
          console.log('Company switch modal open method called');
        } catch (error) {
          console.error('Error opening company switch modal:', error);
        }
      } else {
        console.log('Company switch modal reference is null');
      }
    }, 0);
  }
  
  onCompanySelected(company: any) {
    console.log('Company selected in header component:', company);
    // Find the full company data from our companies array
    const fullCompanyData = this.companies.find(c => c.id === company.id);
    // Update the selected company with full data
    this.selectedCompany = fullCompanyData || company;
    console.log('Updated selected company:', this.selectedCompany);
    // For debugging, let's directly switch the company without confirmation
    // Store in localStorage
    localStorage.setItem('selectedCompany', JSON.stringify(this.selectedCompany));
    // Also store a flag to indicate that the user has manually selected a company
    localStorage.setItem('userSelectedCompany', 'true');
    // Emit event to notify other components about company change
    this.companyChanged.emit(this.selectedCompany);
    // Show success toast message
    this.toastr.success(`Successfully switched to ${this.selectedCompany.name}`, 'Company Switched', {
      timeOut: 3000,
      positionClass: 'toast-top-right'
    });
    // Redirect to home
    this.router.navigate(['/home']);
  }

  testModal() {
    console.log('Test modal button clicked');
    this.openCompanySwitchModal();
  }

}